import React from 'react';
import PropTypes from 'prop-types';
import {
    OpenFeatureProvider,
    useBooleanFlagValue,
} from '@openfeature/react-sdk';

/**
 * Reading feature flags in the UI.
 *
 * This module imports nothing but the OpenFeature React SDK, and that is a
 * constraint rather than a coincidence. Every component that gates on a flag
 * imports it, so anything pulled in here is pulled into all of them: an
 * earlier version imported the service that fetches /api/features, which
 * reaches api.js and initializes Firebase at module scope, and that broke any
 * test rendering a consumer without mocking a feature-flag service it has no
 * reason to know about.
 *
 * Registering the flags lives in shared/services/featureFlagBootstrap.js,
 * which only index.js imports.
 */

/**
 * Whether a feature is on. False for a flag the server did not name, and false
 * if the flags were never registered: an unfinished feature must not appear
 * because a request failed.
 *
 * False is also the answer while bootstrap is still in flight, since the app
 * renders without waiting for it. The hook re-renders when the flags land, so
 * a component that gates on this one must render sensibly with the feature
 * absent — which is what it has to do when the flag is off anyway.
 *
 * @param {string} name
 * @returns {boolean}
 */
export function useFeature(name) {
    return useBooleanFlagValue(name, false);
}

export function FeatureFlagProvider({ children }) {
    return <OpenFeatureProvider>{children}</OpenFeatureProvider>;
}

FeatureFlagProvider.propTypes = {
    children: PropTypes.node,
};

export default FeatureFlagProvider;
